import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  TrainerAttemptListResponseDto,
  TrainerAttemptResultDto,
  TrainerCoverageDto,
} from '@lunary/api-client'
import { api } from '../../shared/api/client'
import { TelegramLinkCard } from './telegram-link-card'
import { TrainerAttemptCard } from './trainer-attempt-card'

export type TrainerCardProps = {
  id: string
  cardName: string
  modelOutput: string
  expertOutput: string | null
  status: string
  rating: number | null
  comment: string
  model: string | null
  promptVersion: string | null
  fewshotUsed: string[] | null
  createdAt: string
  collapsed?: boolean
  onSaved: () => void
}

export function TrainerPage() {
  const qc = useQueryClient()

  const start = useMutation({
    mutationFn: () => api.post<TrainerAttemptResultDto>('/trainer/attempts'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trainer-attempts'] })
      void qc.invalidateQueries({ queryKey: ['trainer-coverage'] })
    },
  })

  const attempts = useQuery({
    queryKey: ['trainer-attempts'],
    queryFn: () => api.get<TrainerAttemptListResponseDto>('/trainer/attempts'),
    retry: false,
  })

  const coverage = useQuery({
    queryKey: ['trainer-coverage'],
    queryFn: () => api.get<TrainerCoverageDto>('/trainer/progress'),
    retry: false,
  })

  const items = attempts.data?.items ?? []
  const cov = coverage.data
  const percent = cov ? Math.round((cov.covered / cov.deck) * 100) : 0

  return (
    <div>
      <p className="muted">
        Случайная карта из 22 старших арканов, трактовка модели, ваша правка, оценка и одобрение. Одобренные примеры
        попадают в подсказку модели и влияют на следующие генерации. Тот же поток доступен в Telegram-боте тренажёра.
      </p>

      <TelegramLinkCard />

      {cov && (
        <div className="card-panel" style={{ marginBottom: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span>
              Покрытие эталонами: {cov.covered}/{cov.deck} карт (у {cov.covered} есть ≥2 одобрённых разбора)
            </span>
            <span className="badge">{percent}%</span>
          </div>
          <div
            className="muted"
            style={{ height: 8, background: 'rgba(255,255,255,0.08)', marginTop: 8, borderRadius: 4, overflow: 'hidden' }}
          >
            <div style={{ width: `${percent}%`, height: '100%', background: 'var(--ok)' }} />
          </div>
          {cov.avg_rating != null && (
            <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
              Средняя оценка: {cov.avg_rating}
            </div>
          )}
        </div>
      )}

      <div className="row" style={{ marginBottom: 12 }}>
        <button disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Генерирую…' : 'Новая случайная карта'}
        </button>
      </div>

      {start.error && (
        <p className="error-box">{start.error instanceof Error ? start.error.message : 'Ошибка генерации'}</p>
      )}

      {start.data && (
        <TrainerAttemptCard
          id={start.data.id}
          cardName={start.data.card.name}
          modelOutput={start.data.content}
          expertOutput={null}
          status="draft"
          rating={null}
          comment=""
          model={start.data.model}
          promptVersion={start.data.prompt_version}
          fewshotUsed={start.data.fewshot_used}
          createdAt={new Date().toISOString()}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['trainer-attempts'] })
            void qc.invalidateQueries({ queryKey: ['trainer-coverage'] })
          }}
        />
      )}
      <h2 style={{ marginTop: 20 }}>Попытки</h2>
      {attempts.isPending && <div className="spinner" aria-label="Загрузка" />}
      {attempts.isError && <p className="error-box">Не удалось загрузить попытки.</p>}
      {items.length === 0 && <p className="muted">Попыток пока нет — сгенерируйте первую карту.</p>}
      {items.map((item) => (
        <TrainerAttemptCard
          key={item.id}
          id={item.id}
          cardName={item.card_id}
          modelOutput={item.model_output}
          expertOutput={item.expert_output}
          status={item.status}
          rating={item.rating}
          comment={item.comment}
          model={item.model}
          promptVersion={item.prompt_version}
          fewshotUsed={null}
          createdAt={item.created_at}
          collapsed
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['trainer-attempts'] })
            void qc.invalidateQueries({ queryKey: ['trainer-coverage'] })
          }}
        />
      ))}
    </div>
  )
}
